// SPDX-License-Identifier: BUSL-1.1

/**
 * Reject confirmation dialog for moderation items.
 *
 * Requires the moderator to provide a deny message before rejecting a
 * tool call. Uses the existing shadcn Dialog + form validation.
 *
 */

import { useState } from 'react';
import { XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';

interface ModerationRejectDialogProps {
  toolName: string;
  isPending: boolean;
  onConfirm: (denyMessage: string) => void;
}

export function ModerationRejectDialog({
  toolName,
  isPending,
  onConfirm,
}: ModerationRejectDialogProps) {
  const [open, setOpen] = useState(false);
  const [denyMessage, setDenyMessage] = useState('');

  const handleReject = () => {
    if (!denyMessage.trim()) return;
    onConfirm(denyMessage.trim());
    setOpen(false);
    setDenyMessage('');
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setDenyMessage('');
      }}
    >
      <DialogTrigger
        render={
          <Button variant="destructive" size="sm" className="gap-1.5" />
        }
      >
        <XCircle className="size-3.5" />
        Reject
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject Tool Call</DialogTitle>
          <DialogDescription>
            Provide a reason for rejecting the{' '}
            <span className="font-mono text-neutral-200">{toolName}</span> call.
            This message will be returned to the agent.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label
            htmlFor="deny-message"
            className="text-sm font-medium text-neutral-300"
          >
            Deny message
          </label>
          <Input
            id="deny-message"
            placeholder="e.g. Insufficient budget for this operation"
            value={denyMessage}
            onChange={(e) => setDenyMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleReject();
            }}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={isPending || !denyMessage.trim()}
            onClick={handleReject}
          >
            {isPending ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
